// vim: set ts=2 sts=2 sw=2 et:

import React, { useState } from "react";
import { connect } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";

import type { GlobalState } from "../../types/stateTypes";
import type { Dispatch } from "redux";
import { buildWebUploadBundle } from "./buildExportBundle";
import { publishAndFuse } from "./syncClient";
import { defaultMyTarima, getDeviceId, getMyTarima } from "./deviceIdentity";
import { setWrpfLastPublished } from "./wrpfSessionActions";

type StateProps = {
  wrpfSession: GlobalState["wrpfSession"];
  global: GlobalState;
};

type DispatchProps = {
  setWrpfLastPublished: (at: string) => void;
};

type Props = StateProps & DispatchProps;

/** Deriva la URL de /publish desde la de /results (misma base de la API). */
function publishUrlFrom(callbackUrl: string): string {
  try {
    return new URL("publish", callbackUrl).toString();
  } catch {
    return callbackUrl.replace(/\/results\/?$/, "/publish");
  }
}

const PublishToWrpfButtonInner = ({ wrpfSession, global, setWrpfLastPublished: markPublished }: Props) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  if (!wrpfSession.tournamentId) {
    return null;
  }

  // Torneo de la web sin sesión (p. ej. un archivo sin sesión cargado en otra PC): el botón queda a la
  // vista, apagado, para que se entienda cómo recuperarlo en vez de desaparecer.
  if (!wrpfSession.callbackUrl || !wrpfSession.callbackToken) {
    return (
      <div style={{ marginTop: "12px" }}>
        <Button variant="success" disabled>
          Enviar resultados a la web
        </Button>
        <div className="small text-muted mt-1">
          Esta PC no tiene sesión con la web. Abrí el torneo desde la web (se conserva lo cargado acá) y
          vas a poder enviar los resultados.
        </div>
      </div>
    );
  }

  const handlePublish = async () => {
    setBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      const tarima = getMyTarima(defaultMyTarima(global.lifting.day, global.lifting.platform));
      const result = await publishAndFuse(publishUrlFrom(wrpfSession.callbackUrl!), wrpfSession.callbackToken!, {
        deviceId: getDeviceId(),
        label: tarima.label,
        ownedPlatforms: [{ day: tarima.day, platform: tarima.platform }],
        bundle: buildWebUploadBundle(global),
      });
      markPublished(new Date().toISOString());
      const pending = result.pendingMatches
        ? ` · ${result.pendingMatches} sin vincular (revisar en la web)`
        : "";
      const meetName = global.meet.name || "el torneo";
      setMessage(
        `Enviado a «${meetName}»: ${result.count} resultados de ${result.devices} tarima(s)${pending}`,
      );
    } catch (err) {
      setIsError(true);
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: "12px" }}>
      <Button variant="success" onClick={handlePublish} disabled={busy}>
        {busy ? "Enviando…" : "Enviar resultados a la web"}
      </Button>
      <div className="small text-muted mt-1">
        Envía los resultados de esta tarima a{" "}
        <strong>«{global.meet.name || "el torneo"}»</strong>; la web los fusiona con las demás y los
        publica.
      </div>
      {message && (
        <Alert variant={isError ? "warning" : "secondary"} className="small mt-2 mb-0 py-2">
          {message}
        </Alert>
      )}
    </div>
  );
};

const mapState = (state: GlobalState): StateProps => ({
  wrpfSession: state.wrpfSession,
  global: state,
});

const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  setWrpfLastPublished: (at) => dispatch(setWrpfLastPublished(at)),
});

export const PublishToWrpfButton = connect(mapState, mapDispatch)(PublishToWrpfButtonInner);

export default PublishToWrpfButton;
